// src/App.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Box } from "@mui/material";
import { useAuth, AuthenticateWithRedirectCallback } from "@clerk/clerk-react";

import Sidebar from "./components/common/Sidebar";
import Header from "./components/common/Header";
import { SidebarProvider } from "./contexts/SidebarContext";
import TariffsPage from "./pages/TariffsPage";
import PredictivePage from "./pages/PredictivePage";
import RouteOptimizationPage from "./pages/RouteOptimizationPage";
import OrdersPage from "./pages/OrdersPage";
import DashboardPage from "./pages/DashboardPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import HelpPage from "./pages/HelpPage";
import RemindersPage from "./pages/RemindersPage";
import SettingsPage from "./pages/SettingsPage";
import AdminPanel from "./components/admin/AdminPanel";
import {
  startInactivityTracker,
  stopInactivityTracker,
} from "./utils/inactivityTracker";

import { setClerkTokenGetter } from "./services/api";
import { useModuleAccess } from "./hooks/useModuleAccess";

// ---------- THEME ----------
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#367C2B",
      light: "#4A8F3A",
      dark: "#2A5F21",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FFD700",
      light: "#FFE44D",
      dark: "#E6C200",
      contrastText: "#000000",
    },
    background: { default: "#FFFFFF", paper: "#FFFFFF" },
    text: { primary: "#1a2027", secondary: "#64748b" },
    success: { main: "#367C2B" },
    warning: { main: "#FFD700" },
    error: { main: "#DC3545" },
    info: { main: "#17A2B8" },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600, color: "#1a2027", fontSize: "1.6rem" },
    h6: { fontWeight: 600, color: "#1a2027", fontSize: "1rem" },
    body1: { fontSize: "0.9rem", lineHeight: 1.5 },
    body2: { fontSize: "0.85rem", lineHeight: 1.4 },
  },
  spacing: 2,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#367C2B",
          color: "#FFFFFF",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.9rem",
        },
        contained: {
          boxShadow: "0 1px 2px rgba(54,124,43,0.15)",
          "&:hover": { boxShadow: "0 2px 4px rgba(54,124,43,0.25)" },
        },
        containedPrimary: {
          backgroundColor: "#367C2B",
          "&:hover": { backgroundColor: "#2A5F21" },
        },
        containedSecondary: {
          backgroundColor: "#FFD700",
          color: "#000000",
          "&:hover": { backgroundColor: "#E6C200" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12, boxShadow: "0 1px 2px rgba(0,0,0,0.06)" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 12, boxShadow: "0 1px 2px rgba(0,0,0,0.06)" },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: { paddingLeft: 8, paddingRight: 8 },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

/**
 * Initializes the Clerk token getter used by api.ts interceptor.
 * This is the ONLY thing needed for attaching Authorization headers.
 */
const ApiAuthInitializer: React.FC = () => {
  const { isLoaded, isSignedIn, getToken } = useAuth();

  useEffect(() => {
    if (!isLoaded) return;

    setClerkTokenGetter(async () => {
      try {
        if (!isSignedIn) return null;
        const t = await getToken({ skipCache: true });
        return t ?? null;
      } catch (e) {
        console.warn("Failed to get Clerk token:", e);
        return null;
      }
    });
  }, [isLoaded, isSignedIn, getToken]);

  return null;
};

// ---------- ROUTE GUARDS (Clerk-only; no Firebase/localStorage) ----------
const PublicOnlyRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return null;
  return isSignedIn ? <Navigate to="/home" replace /> : children;
};

type ModuleKey =
  | "dashboard"
  | "tariff_calculator"
  | "risk_prediction"
  | "route_optimization"
  | "admin_panel";

const ModuleProtectedRoute: React.FC<{ children: React.ReactElement; module: ModuleKey }> = ({
  children,
  module,
}) => {
  const { isLoaded, isSignedIn } = useAuth();
  const { data: modules, isLoading, isError } = useModuleAccess();

  if (!isLoaded) return null;
  if (!isSignedIn) return <Navigate to="/login" replace />;

  if (isLoading) {
    // Avoid flashing unauthorized content while loading permissions
    return null;
  }

  if (isError || !modules) {
    // Prevent blank screens when module API is unavailable.
    return children;
  }

  const allowed = Boolean(modules[module]);
  return allowed ? children : <Navigate to="/help" replace />;
};

// ---------- LAYOUTS ----------
const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      height: "100vh",
      bgcolor: "#F7F7F7",
      overflow: "hidden",
    }}
  >
    <Header />
    <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, pt: "4px", pr: "8px", pb: "8px", pl: "8px", overflowY: "auto", bgcolor: "#F7F7F7" }}>
          {children}
      </Box>
    </Box>
  </Box>
);

const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      bgcolor: "#f8fafc",
      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
      overflowY: "auto",
    }}
  >
    <Box sx={{ flexGrow: 1 }}>{children}</Box>
  </Box>
);



/**
 * Handles inactivity logout using Clerk only.
 * If you want cross-tab sync, rely on Clerk (it updates state across tabs).
 */
const SessionSync: React.FC = () => {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();

  const authed = useMemo(() => (isLoaded ? isSignedIn : false), [isLoaded, isSignedIn]);
  const [authState, setAuthState] = useState(authed);

  useEffect(() => {
    setAuthState(authed);
  }, [authed]);

  useEffect(() => {
    if (!isLoaded) return;

    if (authState) {
      const cleanup = startInactivityTracker(300, () => {
        // You can also call Clerk signOut() here if you want server-side session end.
        navigate("/login", { replace: true });
      });
      return cleanup;
    }

    stopInactivityTracker();
  }, [authState, navigate, isLoaded]);

  return null;
};

// ---------- APP ----------
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={customTheme}>
        <SidebarProvider>
          <BrowserRouter>
            <ApiAuthInitializer />
            <SessionSync />

          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />

            {/* Clerk SSO / OAuth Redirect Callback (Microsoft, etc.) */}
            <Route path="/sso-callback" element={<AuthenticateWithRedirectCallback />} />

            <Route
              path="/login"
              element={
                <PublicOnlyRoute>
                  <PublicLayout>
                    <LoginPage />
                  </PublicLayout>
                </PublicOnlyRoute>
              }
            />

            <Route
              path="/signup"
              element={
                <PublicOnlyRoute>
                  <PublicLayout>
                    <SignupPage />
                  </PublicLayout>
                </PublicOnlyRoute>
              }
            />

            <Route path="/home" element={<Navigate to="/dashboard" replace />} />

            <Route
              path="/dashboard"
              element={
                <ModuleProtectedRoute module="dashboard">
                  <DashboardLayout>
                    <DashboardPage />
                  </DashboardLayout>
                </ModuleProtectedRoute>
              }
            />

            <Route
              path="/tariffs"
              element={
                <ModuleProtectedRoute module="tariff_calculator">
                  <DashboardLayout>
                    <TariffsPage />
                  </DashboardLayout>
                </ModuleProtectedRoute>
              }
            />

            <Route
              path="/predictive"
              element={
                <ModuleProtectedRoute module="risk_prediction">
                  <DashboardLayout>
                    <PredictivePage />
                  </DashboardLayout>
                </ModuleProtectedRoute>
              }
            />

            <Route
              path="/route-optimization"
              element={
                <ModuleProtectedRoute module="route_optimization">
                  <DashboardLayout>
                    <RouteOptimizationPage />
                  </DashboardLayout>
                </ModuleProtectedRoute>
              }
            />

            <Route
              path="/orders"
              element={
                <ModuleProtectedRoute module="dashboard">
                  <DashboardLayout>
                    <OrdersPage />
                  </DashboardLayout>
                </ModuleProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ModuleProtectedRoute module="admin_panel">
                  <DashboardLayout>
                    <AdminPanel />
                  </DashboardLayout>
                </ModuleProtectedRoute>
              }
            />

            <Route
              path="/help"
              element={
                <ModuleProtectedRoute module="dashboard">
                  <DashboardLayout>
                    <HelpPage />
                  </DashboardLayout>
                </ModuleProtectedRoute>
              }
            />

            <Route
              path="/reminders"
              element={
                <ModuleProtectedRoute module="dashboard">
                  <DashboardLayout>
                    <RemindersPage />
                  </DashboardLayout>
                </ModuleProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ModuleProtectedRoute module="dashboard">
                  <DashboardLayout>
                    <SettingsPage />
                  </DashboardLayout>
                </ModuleProtectedRoute>
              }
            />
            
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </BrowserRouter>
        </SidebarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
